// GalleryAssetWrapper.tsx
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Box, Button, Item, AssetType, utils } from "@thenounproject/lingo-core";
import { getAssetError } from "@helpers/getAssetError";
import GalleryAssetErrorButton from "../GalleryAssetErrorButton";
import UseButton from "../UseButton";
import GalleryAssetLayout from "./GalleryAssetLayout";
import { CanvasComponentProps } from "../canvas/sharedProps";

type Layout = "square" | "full";

type Props = {
  layout: Layout;
  children?: React.ReactNode; // caption
  thumbnailWidth?: string;
  onUseButtonSelected?: (item: Item, e: React.MouseEvent) => void;
  backgroundColor?: string;
  isSelected?: boolean;
} & Pick<CanvasComponentProps, "item" | "canEdit" | "onOverflowSelected">;

const Wrapper = styled(Box).attrs({
  borderRadius: "default",
})`
  position: relative;
  width: 100%;

  .action-button {
    box-shadow: 0 1px 3px 0 rgba(36, 36, 37, 0.12) !important;
  }

  ${utils.showOnHoverCSS("action-button")}
`;

const OverflowButton = styled(Button).attrs<{ buttonStyle: string }>(({ buttonStyle }) => {
  return {
    className: "action-button",
    buttonStyle,
    size: "small",
    themeOverrides: {
      primaryColor: "rgba(250, 250, 250, 0.8)",
      primaryColorDark: "white",
      primaryColorTint: "black",
    },
  };
})``;

const GalleryAssetWrapper: React.FC<Props> = ({
  item,
  layout,
  canEdit = false,
  onUseButtonSelected,
  onOverflowSelected,
  backgroundColor,
  thumbnailWidth,
  children,
  isSelected,
}) => {
  const error = getAssetError(item?.asset);
  const menuSource = `overflow-menu-${item.id}`;
  const downloadsAllowed =
    item?.asset?.type === AssetType.color || item.displayProperties?.allowDownload;

  const handleOverflowClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected(item);
    },
    [onOverflowSelected, item]
  );

  const overflowButton = useMemo(() => {
    if (error) return null;
    return (
      <OverflowButton
        dataPopupSource={menuSource}
        icon="navigation.overflow"
        onClick={handleOverflowClick}
        buttonStyle={layout === "square" ? "primary" : "secondary"}
      />
    );
  }, [error, menuSource, handleOverflowClick, layout]);

  const useButton = useMemo(() => {
    if (error) {
      return (
        <GalleryAssetErrorButton
          itemId={item.id}
          assetId={item.asset.id}
          error={error}
          compact
          canEdit={canEdit}
          inspectable={{ item, asset: item.asset }}
        />
      );
    }
    if (!onUseButtonSelected || !downloadsAllowed) return null;
    return <UseButton item={item} onClick={onUseButtonSelected} position="static" />;
  }, [error, item, canEdit, onUseButtonSelected, downloadsAllowed]);

  return (
    <Wrapper
      data-selected={isSelected}
      background={backgroundColor}
      data-testid="gallery-asset-wrapper">
      <GalleryAssetLayout
        item={item}
        layout={layout}
        thumbnailWidth={thumbnailWidth}
        overflowButton={overflowButton}
        useButton={useButton}>
        {children}
      </GalleryAssetLayout>
    </Wrapper>
  );
};

export default GalleryAssetWrapper;
